import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import PropTypes, { object } from 'prop-types';

import BodyDropdownSetting from '../parts/BodyDropdownSetting/BodyDropdownSetting';
import {
  DateTime,
  Table,
  SortingIcon,
  ButtonWithDropdown,
  Member
} from 'components/common/Elements/Elements';

import { usersTableConf } from './helpers/configurates';
import { statusStyle } from './helpers/helpers';
import { jarvisRoleNames } from 'data/constants';

import classes from './UsersSettingsTable.module.scss';
import { hasAccess, resources, access } from 'utils/permissions';

const UsersSettingsTable = ({
  tableData,
  handleManageRole,
  handleSortable,
  direction,
  fieldName
}) => {
  const [row, setRow] = useState('');

  const { t } = useTranslation('settings');

  const getDropdownLinks = user => {
    const { _id, firstName, lastName, role } = user;
    const name = `${firstName} ${lastName}`;

    return [
      {
        label: t('settings:users.table.CHANGE_ROLE_LABEL'),
        handleClick: () => handleManageRole(_id, name, role, 'role'),
        permission: [resources.SETTINGS_USER_CHANGE_ROLE, access.WRITE]
      },
      {
        label: t('settings:users.table.DELETE_USER_LABEL'),
        handleClick: () => handleManageRole(_id, name, role, 'del'),
        permission: [resources.SETTINGS_USERS, access.WRITE]
      }
    ];
  };

  const createMember = user => {
    return {
      fullName: `${user.firstName} ${user.lastName}`,
      description: user.email,
      initials: `${user.firstName[0]}${user.lastName[0]}`,
      initialsColor: user.initialsColor,
      emailVerified: user.status !== 'Pending',
      icon: user.icon ? user.icon : 'NA'
    };
  };

  const mouseEnter = e => {
    setRow(e.currentTarget.getAttribute('r'));
  };

  const mouseLeave = () => {
    setRow('');
  };

  return (
    <Table>
      <Table.Header>
        <Table.Row withBorder={false}>
          {usersTableConf.map(({ isSortable, field, title }, ind) => (
            <Table.HeaderCell
              key={field}
              className={cn(classes.headerCell, classes[field])}
              onMouseEnter={mouseEnter}
              onMouseLeave={mouseLeave}
              r={`r${ind}`}
              title={title}
            >
              {isSortable && (
                <SortingIcon
                  direction={direction}
                  field={field}
                  fieldName={fieldName}
                  onClick={handleSortable(field)}
                />
              )}
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {tableData && (
          <>
            {tableData.map(user => (
              <Table.Row key={user._id}>
                <Table.Cell
                  className={cn(classes.tableCell, row === 'r0' && classes.hov)}
                  onMouseEnter={mouseEnter}
                  classNameContent={cn(
                    user.status === 'Pending' && classes.tableCell_pending
                  )}
                >
                  <Member member={createMember(user)} />
                </Table.Cell>
                <Table.Cell
                  className={cn(
                    statusStyle(user.status),
                    classes.tableCell,
                    row === 'r1' && classes.hov
                  )}
                  onMouseEnter={mouseEnter}
                >
                  {user.status}
                </Table.Cell>
                <Table.Cell
                  className={cn(classes.tableCell, row === 'r2' && classes.hov)}
                  onMouseEnter={mouseEnter}
                >
                  {jarvisRoleNames[user.role] || ''}
                </Table.Cell>
                <Table.Cell
                  className={cn(classes.tableCell, row === 'r3' && classes.hov)}
                  onMouseEnter={mouseEnter}
                >
                  {user._last_login ? (
                    <DateTime date={user._last_login} time />
                  ) : (
                    'NA'
                  )}
                </Table.Cell>
                <Table.Cell
                  className={classes.tableCell_dropDown}
                  onMouseEnter={mouseEnter}
                >
                  {hasAccess([resources.SETTINGS_USERS, access.WRITE]) && (
                    <ButtonWithDropdown
                      tooltipDisabled
                      icon="3dots"
                      className={classes.pointsWrap}
                      buttonClass={classes.points}
                      position={'center'}
                      color={'#536283'}
                      closeOnChange={true}
                    >
                      <BodyDropdownSetting items={getDropdownLinks(user)} />
                    </ButtonWithDropdown>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </>
        )}
      </Table.Body>
    </Table>
  );
};

UsersSettingsTable.propTypes = {
  tableData: PropTypes.arrayOf(object),
  direction: PropTypes.number,
  fieldName: PropTypes.string,
  handleManageRole: PropTypes.func,
  handleDeleteUser: PropTypes.func,
  handleSortable: PropTypes.func
};

export default UsersSettingsTable;
